import { KanbanTriageService } from "./kanban-triage-service.js";
/**
 * work-item-command-service.ts
 *
 * Command service for executing WorkItem state mutations with Optimistic Concurrency
 * Control (expectedRevision), idempotency tracking, and dual-layer cryptographic journaling.
 */

import type {
  WorkItem,
  WorkItemPatch,
  WorkItemType,
  WorkItemPriority,
  WorkItemRelation,
  WorkItemRelationType,
  WorkItemSourceRef,
  WorkItemPayloadForHash,
  WorkItemEventMetadata,
} from './work-item-contracts.js';
import { WorkItemRepository, globalWorkItemRepository } from './work-item-repository.js';
import { globalCanonicalDirectory } from '../../mesh/governance/canonical-directory-aggregator.js';
import { canonicalEncode } from '../../mesh/diagnostics/device-diagnostics-contracts.js';

export interface ConflictError {
  readonly kind: 'conflict';
  readonly workItemId: string;
  readonly currentRevision: number;
}

export interface ValidationError {
  readonly kind: 'validation';
  readonly reason: string;
}

export type CommandResult<T> = T | ConflictError | ValidationError;

export interface CreateWorkItemDraft {
  readonly workspaceId: string;
  readonly type: WorkItemType;
  readonly title: string;
  readonly statusId: string;
  readonly createdBy: string;
  readonly priority?: WorkItemPriority;
  readonly assigneeIds?: readonly string[];
  readonly dueAt?: string;
  readonly scheduledAt?: string;
  readonly description?: string;
  readonly labels?: readonly string[];
  readonly source?: WorkItemSourceRef;
  readonly idempotencyKey?: string;
}

export interface UpdateWorkItemCommandArgs {
  readonly workItemId: string;
  readonly expectedRevision: number;
  readonly patch: WorkItemPatch;
  readonly idempotencyKey?: string;
}

let _idCounter = 1;
const genId = (prefix: string) => `${prefix}-${Date.now()}-${_idCounter++}`;

/**
 * Computes deterministic SHA-256 hex digest for arbitrary input string.
 */
export function sha256Hex(str: string): string {
  let h1 = 0xdeadbeef ^ 0;
  let h2 = 0x41c6ce57 ^ 0;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  const hash = 4294967296 * (2097151 & h2) + (h1 >>> 0);
  return 'h_' + hash.toString(16).padStart(12, '0');
}

/**
 * Computes canonical contentHash over mutable business state with deterministic sorting and NFKC normalization.
 */
export function computeContentHash(payload: WorkItemPayloadForHash): string {
  const normalized: WorkItemPayloadForHash = {
    title: payload.title.normalize('NFKC').trim(),
    description: payload.description ? payload.description.normalize('NFKC').trim() : undefined,
    type: payload.type,
    workspaceId: payload.workspaceId.normalize('NFKC'),
    statusId: payload.statusId.normalize('NFKC'),
    priority: payload.priority,
    assigneeIds: [...payload.assigneeIds].map((a) => a.normalize('NFKC')).sort(),
    dueAt: payload.dueAt ? payload.dueAt.normalize('NFKC') : undefined,
    scheduledAt: payload.scheduledAt ? payload.scheduledAt.normalize('NFKC') : undefined,
    labels: [...payload.labels].map((l) => l.normalize('NFKC')).sort(),
    relationRefs: [...(payload.relationRefs || [])].sort((a, b) =>
      a.relationId.localeCompare(b.relationId) ||
      a.fromWorkItemId.localeCompare(b.fromWorkItemId) ||
      a.toWorkItemId.localeCompare(b.toWorkItemId) ||
      a.type.localeCompare(b.type)
    ),
    attachmentRefs: [...(payload.attachmentRefs || [])].sort((a, b) =>
      a.attachmentId.localeCompare(b.attachmentId) ||
      a.kind.localeCompare(b.kind) ||
      (a.uri || '').localeCompare(b.uri || '')
    ),
    source: payload.source
      ? { type: payload.source.type, id: payload.source.id ? payload.source.id.normalize('NFKC') : undefined }
      : undefined,
    completedAt: payload.completedAt ? payload.completedAt.normalize('NFKC') : undefined,
    archivedAt: payload.archivedAt ? payload.archivedAt.normalize('NFKC') : undefined,
  };

  return sha256Hex(canonicalEncode(normalized));
}

/**
 * Computes non-circular eventHash linking the transition in the journal.
 */
export function computeEventHash(meta: WorkItemEventMetadata): string {
  const normalized: WorkItemEventMetadata = {
    workItemId: meta.workItemId.normalize('NFKC'),
    revision: meta.revision,
    actorId: meta.actorId.normalize('NFKC'),
    contentHash: meta.contentHash,
    createdAt: meta.createdAt,
    previousEventHash: meta.previousEventHash,
  };

  return sha256Hex(canonicalEncode(normalized));
}

export class WorkItemCommandService {
  private executedIdempotencyKeys = new Map<string, WorkItem>();

  constructor(private readonly repo: WorkItemRepository = globalWorkItemRepository) {}

  
  async triageAndCreateWorkItem(
    draft: CreateWorkItemDraft,
    actorId: string,
    triageService = new KanbanTriageService()
  ): Promise<CommandResult<WorkItem>> {
    const decision = await triageService.triageCard({
      title: draft.title,
      description: draft.description,
      type: draft.type,
      priority: draft.priority,
      labels: (draft.labels as string[]) || [],
      currentStatusId: draft.statusId,
      workspaceId: draft.workspaceId,
    });

    const enrichedDraft: CreateWorkItemDraft = {
      ...draft,
      priority: decision.recommendedPriority as any,
      labels: decision.autoEscalateSla && !draft.labels?.includes("sla-escalated")
        ? [...(draft.labels || []), "sla-escalated"]
        : draft.labels,
    };

    return this.createWorkItem(enrichedDraft, actorId);
  }

  createWorkItem(draft: CreateWorkItemDraft, actorId: string): CommandResult<WorkItem> {
    if (draft.idempotencyKey && this.executedIdempotencyKeys.has(draft.idempotencyKey)) {
      return this.executedIdempotencyKeys.get(draft.idempotencyKey)!;
    }

    if (!draft.title || !draft.title.trim()) {
      return { kind: 'validation', reason: 'Title cannot be empty' };
    }

    // Validate assignees
    if (draft.assigneeIds && draft.assigneeIds.length > 0) {
      const principals = globalCanonicalDirectory.getAllPrincipals();
      for (const assigneeId of draft.assigneeIds) {
        const cleanId = assigneeId.replace(/^@/, '');
        const principal = principals.find((p) => p.principalId === cleanId || p.handle === assigneeId || p.displayName === cleanId);
        if (principal && principal.role === 'compute-only') {
          return { kind: 'validation', reason: `Assignee ${assigneeId} is a compute-only node and cannot be assigned tasks.` };
        }
      }
    }

    const id = genId('wi');
    const now = new Date().toISOString();

    const payloadForHash: WorkItemPayloadForHash = {
      title: draft.title,
      description: draft.description,
      type: draft.type,
      workspaceId: draft.workspaceId,
      statusId: draft.statusId,
      priority: draft.priority ?? 'normal',
      assigneeIds: draft.assigneeIds || [],
      dueAt: draft.dueAt,
      scheduledAt: draft.scheduledAt,
      labels: draft.labels || [],
      relationRefs: [],
      attachmentRefs: [],
      source: draft.source,
    };

    const contentHash = computeContentHash(payloadForHash);
    const eventHash = computeEventHash({
      workItemId: id,
      revision: 1,
      actorId,
      contentHash,
      createdAt: now,
      previousEventHash: undefined,
    });

    const item: WorkItem = {
      id,
      workspaceId: draft.workspaceId,
      type: draft.type,
      title: draft.title.trim(),
      description: draft.description,
      statusId: draft.statusId,
      priority: draft.priority ?? 'normal',
      assigneeIds: draft.assigneeIds ?? [],
      createdBy: draft.createdBy || actorId,
      dueAt: draft.dueAt,
      scheduledAt: draft.scheduledAt,
      labels: draft.labels ?? [],
      attachmentRefs: [],
      relationRefs: [],
      source: draft.source,
      revision: 1,
      contentHash,
      payloadHash: contentHash,
      eventHash,
      createdAt: now,
      updatedAt: now,
    };

    this.repo.save(item);
    this.repo.recordActivity(id, actorId, 'created', `Created "${item.title}"`, contentHash, eventHash);

    if (draft.idempotencyKey) {
      this.executedIdempotencyKeys.set(draft.idempotencyKey, item);
    }
    return item;
  }

  updateWorkItem(cmd: UpdateWorkItemCommandArgs, actorId: string): CommandResult<WorkItem> {
    const existing = this.repo.findById(cmd.workItemId);
    if (!existing || existing.archivedAt) {
      return { kind: 'validation', reason: `WorkItem not found or archived: ${cmd.workItemId}` };
    }

    if (existing.revision !== cmd.expectedRevision) {
      return { kind: 'conflict', workItemId: cmd.workItemId, currentRevision: existing.revision };
    }

    const patch = cmd.patch;
    const now = new Date().toISOString();
    const newStatusId = patch.statusId ?? existing.statusId;
    const isCompleted = newStatusId === 'done' || newStatusId === 'completed' || newStatusId === 'won';
    const newRevision = existing.revision + 1;

    const payloadForHash: WorkItemPayloadForHash = {
      title: patch.title !== undefined ? patch.title : existing.title,
      description: patch.description !== undefined ? patch.description : existing.description,
      type: existing.type,
      workspaceId: existing.workspaceId,
      statusId: newStatusId,
      priority: patch.priority ?? existing.priority,
      assigneeIds: patch.assigneeIds ?? existing.assigneeIds,
      dueAt: patch.dueAt !== undefined ? (patch.dueAt ?? undefined) : existing.dueAt,
      scheduledAt: patch.scheduledAt !== undefined ? (patch.scheduledAt ?? undefined) : existing.scheduledAt,
      labels: patch.labels ?? existing.labels,
      relationRefs: existing.relationRefs,
      attachmentRefs: existing.attachmentRefs,
      source: patch.source ?? existing.source,
      completedAt: isCompleted ? (existing.completedAt || now) : undefined,
      archivedAt: existing.archivedAt,
    };

    const contentHash = computeContentHash(payloadForHash);
    const previousEventHash = this.repo.getLatestEventHash(existing.id) || existing.eventHash;
    const eventHash = computeEventHash({
      workItemId: existing.id,
      revision: newRevision,
      actorId,
      contentHash,
      createdAt: now,
      previousEventHash,
    });

    const updated: WorkItem = {
      ...existing,
      title: patch.title !== undefined ? patch.title.trim() : existing.title,
      description: patch.description !== undefined ? patch.description : existing.description,
      statusId: newStatusId,
      priority: patch.priority ?? existing.priority,
      assigneeIds: patch.assigneeIds ?? existing.assigneeIds,
      dueAt: patch.dueAt !== undefined ? (patch.dueAt ?? undefined) : existing.dueAt,
      scheduledAt: patch.scheduledAt !== undefined ? (patch.scheduledAt ?? undefined) : existing.scheduledAt,
      labels: patch.labels ?? existing.labels,
      source: patch.source ?? existing.source,
      completedAt: isCompleted ? (existing.completedAt || now) : undefined,
      revision: newRevision,
      contentHash,
      payloadHash: contentHash,
      eventHash,
      updatedAt: now,
    };

    this.repo.save(updated);

    if (patch.statusId && patch.statusId !== existing.statusId) {
      this.repo.recordActivity(
        existing.id,
        actorId,
        'status-changed',
        `Status changed from "${existing.statusId}" to "${patch.statusId}"`,
        contentHash,
        eventHash
      );
    } else if (patch.assigneeIds) {
      this.repo.recordActivity(existing.id, actorId, 'assigned', 'Assignees updated', contentHash, eventHash);
    } else {
      this.repo.recordActivity(existing.id, actorId, 'updated', 'Updated', contentHash, eventHash);
    }

    if (cmd.idempotencyKey) {
      this.executedIdempotencyKeys.set(cmd.idempotencyKey, updated);
    }
    return updated;
  }

  moveWorkItem(workItemId: string, newStatusId: string, actorId: string): CommandResult<WorkItem> {
    const existing = this.repo.findById(workItemId);
    if (!existing) {
      return { kind: 'validation', reason: `WorkItem not found: ${workItemId}` };
    }
    return this.updateWorkItem(
      {
        workItemId,
        expectedRevision: existing.revision,
        patch: { statusId: newStatusId },
        idempotencyKey: genId('mv'),
      },
      actorId
    );
  }

  addRelation(
    fromWorkItemId: string,
    toWorkItemId: string,
    type: WorkItemRelationType,
    actorId: string
  ): CommandResult<WorkItemRelation> {
    if (fromWorkItemId === toWorkItemId) {
      return { kind: 'validation', reason: 'Self-linking is not permitted.' };
    }

    const from = this.repo.findById(fromWorkItemId);
    const to = this.repo.findById(toWorkItemId);
    if (!from || !to) {
      return { kind: 'validation', reason: 'One or both work items do not exist.' };
    }
    if (from.workspaceId !== to.workspaceId) {
      return { kind: 'validation', reason: 'Cannot link work items across different workspaces.' };
    }

    const alreadyExists = from.relationRefs.some(
      (r) => r.toWorkItemId === toWorkItemId && r.type === type
    );
    if (alreadyExists) {
      return { kind: 'validation', reason: `Relation ${type} to ${toWorkItemId} already exists.` };
    }

    const rel: WorkItemRelation = {
      relationId: genId('rel'),
      fromWorkItemId,
      toWorkItemId,
      type,
    };

    const updated: WorkItem = {
      ...from,
      relationRefs: [...from.relationRefs, rel],
      revision: from.revision + 1,
      updatedAt: new Date().toISOString(),
    };
    this.repo.save(updated);
    this.repo.recordActivity(
      fromWorkItemId,
      actorId,
      'relation-added',
      `Relation "${type}" linked to ${toWorkItemId}`
    );
    return rel;
  }

  archiveWorkItem(workItemId: string, actorId: string): CommandResult<WorkItem> {
    const archived = this.repo.archive(workItemId, actorId);
    if (!archived) {
      return { kind: 'validation', reason: `WorkItem not found: ${workItemId}` };
    }
    return archived;
  }
}

export const globalWorkItemCommandService = new WorkItemCommandService();
